import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'

export type ThemeId = 'brand' | 'navy' | 'graphite' | 'crimson' | 'amber' | 'berry'
export type FontSizeId = 'compact' | 'default' | 'large' | 'xlarge'

/**
 * 主题清单 —— **唯一真源**。
 *
 * 加一套主题 = 这里加一条 + `tokens.css` 加一个 `:root[data-theme="<id>"]` 块，
 * **两处都要**（少了令牌块，切过去会静默停在默认配色上，页面看着"点了没反应"）。
 * 每套都必须是浅色：本项目不接受深色底（长时看图时缩略图会被暗底带偏）。
 *
 * 2026-09-30 用户口径「增加一些别的，红色，橙色什么的」→ 补朱砂红 / 暖阳橙 / 莓粉。
 * 新增的三套**危险色都往后撤了一档**（见 tokens.css 里的注释）：
 * 红橙粉的主色离 `--destructive` 太近，「移除记录」会被看成和主按钮同类。
 */
export const THEMES: { id: ThemeId; name: string; desc: string; swatch: string[] }[] = [
  { id: 'brand', name: '华数蓝', desc: '电光蓝 #3260FF，品牌默认', swatch: ['#3260FF', '#F6F9FF', '#1A2C4C'] },
  { id: 'navy', name: '深海蓝', desc: '深海军蓝 #002A7E，稳重内敛', swatch: ['#002A7E', '#EDF3FF', '#0E1F45'] },
  { id: 'graphite', name: '石墨', desc: '中性灰蓝，适合长时看图', swatch: ['#4A5568', '#F6F7F9', '#1B2230'] },
  { id: 'crimson', name: '朱砂红', desc: '朱砂 #D93A3A，促销氛围', swatch: ['#D93A3A', '#FFF6F5', '#4A1C1C'] },
  { id: 'amber', name: '暖阳橙', desc: '暖橙 #F5700F，暖调创作氛围', swatch: ['#F5700F', '#FFF8F2', '#3D2408'] },
  { id: 'berry', name: '莓粉', desc: '莓粉 #E23F86，女装 / 美妆场景', swatch: ['#E23F86', '#FFF5F9', '#43142B'] },
]

export const FONT_SIZES: { id: FontSizeId; name: string; px: number }[] = [
  { id: 'compact', name: '紧凑', px: 14 },
  { id: 'default', name: '标准', px: 15 },
  { id: 'large', name: '较大', px: 16.5 },
  { id: 'xlarge', name: '特大', px: 18 },
]

type SettingsState = {
  theme: ThemeId
  fontSize: FontSizeId
  sidebarCollapsed: boolean
  compactTable: boolean
}

/**
 * ⚠️ 这里曾经有一份 `llm`（文案模型的地址 / Key / 模型名），**已删除**。
 *
 * 删的原因不是「没用」，而是它**是个假控件**：
 *   · 值只写在 localStorage，**从来没有发给后端**，改完什么都不会发生；
 *   · 「测试连通」按钮是个 `setTimeout(900)`，不管 Key 对不对都报「连通成功」。
 * 现在文案模型由 `PUT /api/admin/upstreams/llm` 真改后端（后台管理页），
 * 同一个字段留两个入口的后果是：用户在后一个入口改完、发现没生效、
 * 于是回到前一个入口再改一遍 —— 而那个入口**永远不会有任何效果**。
 */
const DEFAULTS: SettingsState = {
  theme: 'brand',
  fontSize: 'default',
  sidebarCollapsed: false,
  compactTable: false,
}

type SettingsApi = {
  settings: SettingsState
  setTheme: (t: ThemeId) => void
  setFontSize: (f: FontSizeId) => void
  toggleSidebar: () => void
  setCompactTable: (v: boolean) => void
  resetAll: () => void
}

const Ctx = createContext<SettingsApi | null>(null)

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings, resetAll] = useLocalStorage<SettingsState>('settings_v1', DEFAULTS)

  // 把主题/字号写到 <html>，由 tokens.css 的 [data-theme] / [data-font-size] 接管
  // 主题表迭代过（旧值 forest 已下线），此处做一次兜底，避免历史 localStorage 命中空主题
  const theme: ThemeId = THEMES.some((t) => t.id === settings.theme) ? settings.theme : 'brand'
  const fontSize = settings.fontSize
  if (typeof document !== 'undefined') {
    const el = document.documentElement
    if (el.getAttribute('data-theme') !== theme) el.setAttribute('data-theme', theme)
    if (el.getAttribute('data-font-size') !== fontSize) el.setAttribute('data-font-size', fontSize)
  }

  const value = useMemo<SettingsApi>(
    () => ({
      settings,
      setTheme: (theme) => setSettings((s) => ({ ...s, theme })),
      setFontSize: (fontSize) => setSettings((s) => ({ ...s, fontSize })),
      toggleSidebar: () => setSettings((s) => ({ ...s, sidebarCollapsed: !s.sidebarCollapsed })),
      setCompactTable: (compactTable) => setSettings((s) => ({ ...s, compactTable })),
      resetAll,
    }),
    [settings, setSettings, resetAll],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSettings() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider')
  return ctx
}

/** 供非组件环境读取当前主题 */
export function useThemeAttr() {
  const { settings } = useSettings()
  return settings.theme
}
